import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, Camera, MapPin, CheckCircle2, Clock, X, HelpCircle, RotateCcw } from "lucide-react";

const S = {
  en: { today: "Today", checkin: "I'm here — check in", onsite: "On site", done: "All done",
        complete: "Mark this service done", photo: "Add a photo", needPhoto: "Add at least one photo — this service needs proof.",
        items: "Checklist", finished: "Finished", morning: "Morning",
        scheduled: "Scheduled", missed: "Missed",
        reported: "Reported since your last visit", itemDone: "Done", itemCant: "Can't do it",
        itemNotMine: "Not ours", itemPhoto: "Photo", rolled: "still open from last time",
        allClear: "Nothing new was reported." },
  es: { today: "Hoy", checkin: "Llegué — registrar entrada", onsite: "En sitio", done: "Terminado",
        complete: "Marcar este servicio como terminado", photo: "Agregar una foto", needPhoto: "Agrega al menos una foto — este servicio requiere prueba.",
        items: "Lista", finished: "Terminado", morning: "Buenos días",
        scheduled: "Programado", missed: "Perdido",
        reported: "Reportado desde su última visita", itemDone: "Hecho", itemCant: "No puedo",
        itemNotMine: "No es nuestro", itemPhoto: "Foto", rolled: "pendiente de la última vez",
        allClear: "No hay nada nuevo reportado." },
};

export default function VisitPage() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [target, setTarget] = useState(null);
  const [itemTarget, setItemTarget] = useState(null);
  const fileRef = useRef(null);
  const itemFileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/fs/visit/${token}`); setD(data); }
    catch { setD({ error: true }); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="grid min-h-screen place-items-center bg-sand-50"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  if (d.error) return (
    <div className="grid min-h-screen place-items-center bg-sand-50 px-6 text-center">
      <p className="text-base text-ink/60">That link isn't valid any more.</p>
    </div>
  );
  const t = S[d.language === "es" ? "es" : "en"];

  const op = async (o, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fs/visit/${token}/action/${o}`, body || {});
      if (data.needs_photo) toast.error(t.needPhoto);
      await load();
    } catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };
  // The Tuesday loop: things residents reported since this crew was last here. Closing one
  // resolves the resident's own request and texts them a photo — that's the whole point.
  const itemOp = async (id, o, body) => {
    setBusy(true);
    try {
      await api.post(`/fs/visit/${token}/item/${id}/${o}`, body || {});
      await load();
      toast.success("✓");
    } catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };
  const itemPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !itemTarget) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/fs/visit/${token}/item/${itemTarget}/photo`, fd); await load(); toast.success("✓"); }
    catch { toast.error("Photo didn't upload"); }
    setBusy(false);
    e.target.value = "";
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !target) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("visit_id", target);
    try { await api.post(`/fs/visit/${token}/photo`, fd); await load(); toast.success("✓"); }
    catch { toast.error("Photo didn't upload"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-sand-50 pb-12" data-testid="visit-page">
      <div className="mx-auto max-w-md px-5 pt-9">
        <div className="text-xs font-bold uppercase tracking-widest text-redline-600">{t.today} · {d.date}</div>
        <h1 className="mt-1.5 font-heading text-2xl font-extrabold text-ink">
          {t.morning}{d.tech ? ` ${d.tech}` : ""}
        </h1>
        <div className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-ink/50">
          <MapPin className="h-3.5 w-3.5" />{d.community}
        </div>

        {(d.items || []).length > 0 && (
          <div className="mt-6 rounded-2xl bg-white p-5" data-testid="visit-items">
            <div className="text-xs font-bold uppercase tracking-wider text-redline-600">{t.reported}</div>
            <div className="mt-3 space-y-2">
              {d.items.map((it) => (
                <div key={it.id} data-testid={`visit-queue-item-${it.id}`}
                  className={`rounded-xl px-4 py-3 ${it.status === "on_visit" ? "bg-sand-100" : "bg-sand-50 opacity-60"}`}>
                  <div className="text-sm font-bold text-ink">{it.summary}</div>
                  <div className="mt-0.5 text-xs text-ink/50">
                    {it.location ? `${it.location} · ` : ""}{it.reported_by}
                    {(it.rolled_count || 0) > 0 && (
                      <span className="ml-1 inline-flex items-center gap-1 font-bold text-amber-700">
                        <RotateCcw className="h-3 w-3" /> {t.rolled}
                      </span>
                    )}
                  </div>
                  {(it.photo_urls || []).length > 0 && (
                    <div className="mt-2 flex gap-2 overflow-x-auto">
                      {it.photo_urls.map((u, i) => <img key={i} src={u} alt="" className="h-16 w-16 shrink-0 rounded-lg object-cover" />)}
                    </div>
                  )}
                  {it.proof_url && <img src={it.proof_url} alt="proof" className="mt-2 h-16 w-16 rounded-lg object-cover" />}
                  {it.status === "on_visit" ? (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button data-testid={`visit-item-done-${it.id}`} disabled={busy}
                        onClick={() => itemOp(it.id, "done", {})}
                        className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3.5 text-sm font-bold text-white active:bg-emerald-700 disabled:opacity-40">
                        <Check className="h-4 w-4" /> {t.itemDone}
                      </button>
                      <button data-testid={`visit-item-photo-${it.id}`} disabled={busy}
                        onClick={() => { setItemTarget(it.id); setTimeout(() => itemFileRef.current?.click(), 0); }}
                        className="flex items-center justify-center gap-1.5 rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-xs font-bold text-ink/70 disabled:opacity-40">
                        <Camera className="h-4 w-4" /> {t.itemPhoto}
                      </button>
                      <button data-testid={`visit-item-cant-${it.id}`} disabled={busy}
                        onClick={() => itemOp(it.id, "cant", { reason: window.prompt("Why not?") || "" })}
                        className="flex items-center justify-center gap-1.5 rounded-xl border border-sand-300 bg-white px-3 py-2.5 text-xs font-bold text-ink/70 disabled:opacity-40">
                        <X className="h-4 w-4" /> {t.itemCant}
                      </button>
                      <button data-testid={`visit-item-notmine-${it.id}`} disabled={busy}
                        onClick={() => itemOp(it.id, "not-mine", {})}
                        className="col-span-2 flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold text-ink/45 disabled:opacity-40">
                        <HelpCircle className="h-3.5 w-3.5" /> {t.itemNotMine}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs font-bold uppercase tracking-wide text-ink/40">{it.status}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 space-y-4">
          {(d.visits || []).map((v) => {
            const state = v.checklist_state || {};
            const allDone = (v.checklist || []).length > 0 && (v.checklist || []).every((i) => state[i]);
            return (
              <div key={v.id} data-testid={`visit-card-${v.id}`} className="rounded-2xl bg-white p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-heading text-lg font-bold text-ink">{v.name}</div>
                    <div className="text-xs font-semibold text-ink/45">{v.window?.start}–{v.window?.end}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${
                    v.status === "done" ? "bg-emerald-50 text-emerald-700"
                      : v.status === "missed" ? "bg-red-50 text-red-700"
                        : v.status === "checked_in" ? "bg-indigo-50 text-indigo-700" : "bg-sand-100 text-ink/50"}`}>
                    {v.status === "checked_in" ? t.onsite : v.status === "done" ? t.done
                      : v.status === "missed" ? t.missed : t.scheduled}
                  </span>
                </div>

                {v.status === "scheduled" && (
                  <button data-testid={`visit-checkin-${v.id}`} onClick={() => op("checkin", { visit_id: v.id })} disabled={busy}
                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-redline-500 px-5 py-4 text-base font-bold text-white active:bg-redline-600 disabled:opacity-50">
                    <Clock className="h-5 w-5" /> {t.checkin}
                  </button>
                )}

                {(v.checklist || []).length > 0 && v.status !== "scheduled" && (
                  <div className="mt-4">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/40">{t.items}</div>
                    <div className="space-y-1.5">
                      {(v.checklist || []).map((item) => {
                        const on = !!state[item];
                        return (
                          <button key={item} data-testid={`visit-item-${v.id}-${item.slice(0, 12)}`}
                            onClick={() => op("item", { visit_id: v.id, key: item, done: !on })}
                            className={`flex w-full items-center gap-2.5 rounded-xl px-4 py-3 text-left text-sm font-semibold transition-colors ${on ? "bg-emerald-600 text-white" : "bg-sand-100 text-ink/60"}`}>
                            <Check className="h-4 w-4 shrink-0" />{item}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {v.status !== "scheduled" && v.status !== "done" && (
                  <div className="mt-4 space-y-2">
                    {(v.photo_urls || []).length > 0 && (
                      <div className="flex gap-2 overflow-x-auto">
                        {v.photo_urls.map((u, i) => <img key={i} src={u} alt="proof" className="h-20 w-20 shrink-0 rounded-lg object-cover" />)}
                      </div>
                    )}
                    <button data-testid={`visit-photo-${v.id}`} onClick={() => { setTarget(v.id); setTimeout(() => fileRef.current?.click(), 0); }} disabled={busy}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl border border-sand-300 bg-white px-5 py-3.5 text-sm font-bold text-ink disabled:opacity-50">
                      <Camera className="h-5 w-5" /> {t.photo}
                      {v.photo_required ? " *" : ""}
                    </button>
                    <button data-testid={`visit-complete-${v.id}`} onClick={() => op("complete", { visit_id: v.id })} disabled={busy || (!!(v.checklist || []).length && !allDone)}
                      className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-4 text-base font-bold text-white active:bg-emerald-700 disabled:opacity-40">
                      <Check className="h-5 w-5" /> {t.complete}
                    </button>
                  </div>
                )}

                {v.status === "done" && (
                  <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" /> {t.finished}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={upload} className="hidden" data-testid="visit-file-input" />
        <input ref={itemFileRef} type="file" accept="image/*" capture="environment" onChange={itemPhoto} className="hidden" data-testid="visit-item-file-input" />
      </div>
    </div>
  );
}
